import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useAuth } from "../../contexts/AuthContext";
import {
  Overlay,
  ModalContainer,
  ScrollableContent,
  Header,
  ProgressText,
  CloseButton,
  CardImage,
  CardBody,
  CardTitle,
  CardDirector,
  GenreList,
  GenreChip,
  CardDescription,
  ActionsContainer,
  ActionButtonRow,
  PriorityButton,
  SkipButton,
  OptionsRow,
  ToggleLabel,
  ShuffleButton,
  CompletionContainer,
  CompletionText,
  CompletionCloseButton,
  StyledBookmarkIcon,
  StyledBookmarkBorderIcon,
  StyledStarRoundedIcon,
  StyledStarBorderRoundedIcon,
} from "./QuickReview.styled";
import DirectorLink from "../common/DirectorLink";

function QuickReview({ films, priorities, onPriorityChange, onClose }) {
  const { token } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [skipRated, setSkipRated] = useState(false);
  const [isShuffled, setIsShuffled] = useState(false);
  const [shuffledOrder, setShuffledOrder] = useState(null);

  const filmQueue = useMemo(() => {
    let queue = [...films];

    if (isShuffled && shuffledOrder) {
      queue = shuffledOrder
        .map((id) => films.find((f) => f.film_id === id))
        .filter(Boolean);
    }

    if (skipRated) {
      queue = queue.filter((film) => {
        const p = priorities[film.film_id];
        return p === undefined || p === 0 || p === 5;
      });
    }

    return queue;
  }, [films, priorities, skipRated, isShuffled, shuffledOrder]);

  const currentFilm = filmQueue[currentIndex] || null;
  const isComplete = currentIndex >= filmQueue.length;

  const advance = useCallback(() => {
    setCurrentIndex((prev) => prev + 1);
  }, []);

  const handlePriority = useCallback(
    async (newPriority) => {
      if (!currentFilm || !token) return;

      const filmId = currentFilm.film_id;
      const currentPriority = priorities[filmId];
      const priorityToSend = newPriority === currentPriority ? 0 : newPriority;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/userfilms`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              film_id: filmId,
              priority: priorityToSend,
            }),
          },
        );

        if (response.ok) {
          const data = await response.json();
          onPriorityChange(filmId, data.priority);
        }
      } catch (error) {
        console.error("Error setting priority:", error);
      }

      advance();
    },
    [currentFilm, token, priorities, onPriorityChange, advance],
  );

  const handleShuffle = useCallback(() => {
    const remaining = filmQueue.slice(currentIndex).map((f) => f.film_id);
    const seen = isShuffled && shuffledOrder
      ? shuffledOrder.slice(0, currentIndex)
      : films.slice(0, currentIndex).map((f) => f.film_id);

    // Fisher-Yates shuffle on remaining
    for (let i = remaining.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [remaining[i], remaining[j]] = [remaining[j], remaining[i]];
    }

    setShuffledOrder([...seen, ...remaining]);
    setIsShuffled(true);
  }, [filmQueue, currentIndex, isShuffled, shuffledOrder, films]);

  const handleSkipRatedToggle = useCallback(() => {
    setSkipRated((prev) => !prev);
    setCurrentIndex(0);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isComplete) {
        if (e.key === "Escape") onClose();
        return;
      }

      switch (e.key) {
        case "ArrowLeft":
          handlePriority(1); // Maybe
          break;
        case "ArrowRight":
          handlePriority(2); // Definitely
          break;
        case "ArrowDown":
        case " ":
          e.preventDefault();
          advance();
          break;
        case "Escape":
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isComplete, handlePriority, advance, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const currentPriority = currentFilm
    ? priorities[currentFilm.film_id]
    : undefined;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <ProgressText>
            {isComplete
              ? `${filmQueue.length} / ${filmQueue.length} films`
              : `${currentIndex + 1} / ${filmQueue.length} films`}
          </ProgressText>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </Header>

        {isComplete ? (
          <CompletionContainer>
            <CompletionText>You've reviewed all films!</CompletionText>
            <CompletionCloseButton onClick={onClose}>
              Close
            </CompletionCloseButton>
          </CompletionContainer>
        ) : (
          currentFilm && (
            <>
              <ScrollableContent>
                {currentFilm.image && (
                  <CardImage src={currentFilm.image} alt={currentFilm.title} />
                )}
                <CardBody>
                  <CardTitle>{currentFilm.title}</CardTitle>
                  {currentFilm.director && (
                    <CardDirector>
                      Directed by: <DirectorLink director={currentFilm.director} />
                    </CardDirector>
                  )}
                  {currentFilm.genres && currentFilm.genres.length > 0 && (
                    <GenreList>
                      {currentFilm.genres.map((g) => (
                        <GenreChip key={g.genre_id}>{g.genre_name}</GenreChip>
                      ))}
                    </GenreList>
                  )}
                  {currentFilm.description && (
                    <CardDescription>
                      {currentFilm.description}
                    </CardDescription>
                  )}
                </CardBody>
              </ScrollableContent>
              <ActionsContainer>
                <ActionButtonRow>
                  <PriorityButton
                    $buttonType="maybe"
                    $active={currentPriority === 1}
                    onClick={() => handlePriority(1)}
                  >
                    {currentPriority === 1 ? (
                      <StyledBookmarkIcon />
                    ) : (
                      <StyledBookmarkBorderIcon />
                    )}
                    Maybe
                  </PriorityButton>
                  <PriorityButton
                    $buttonType="definitely"
                    $active={currentPriority === 2}
                    onClick={() => handlePriority(2)}
                  >
                    {currentPriority === 2 ? (
                      <StyledStarRoundedIcon />
                    ) : (
                      <StyledStarBorderRoundedIcon />
                    )}
                    Definitely
                  </PriorityButton>
                </ActionButtonRow>
                <SkipButton onClick={advance}>Skip / Next Film</SkipButton>
              </ActionsContainer>
            </>
          )
        )}

        <OptionsRow>
          <ToggleLabel>
            <input
              type="checkbox"
              checked={skipRated}
              onChange={handleSkipRatedToggle}
            />
            Skip already-rated
          </ToggleLabel>
          <ShuffleButton onClick={handleShuffle}>Shuffle</ShuffleButton>
        </OptionsRow>
      </ModalContainer>
    </Overlay>
  );
}

QuickReview.propTypes = {
  films: PropTypes.array.isRequired,
  priorities: PropTypes.object.isRequired,
  onPriorityChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default QuickReview;
